import React, { useEffect, useRef, useState } from "react";
import { SparklesIcon, MicrophoneIcon } from "@heroicons/react/24/solid";
import {
  getLiveGenerativeModel,
  startAudioConversation,
  ResponseModality,
  type LiveSession,
  type AudioConversationController,
  type Tool,
  type FunctionCall,
  type FunctionResponse,
  type FunctionDeclaration,
} from "firebase/ai";
import { aiGoogle } from "../firebase/config";

// One log action the AI is allowed to perform on the user's behalf. Pair the
// public `declaration` (sent to the model) with a local `handler` that
// actually does the work in the React app.
export interface AILogFunction {
  declaration: FunctionDeclaration;
  handler: (
    args: Record<string, unknown>,
  ) => Promise<AILogFunctionResult> | AILogFunctionResult;
}

// Plain-data result a handler returns. Anything in `data` is forwarded to the
// model so it can read back numbers/IDs in its short confirmation reply.
export interface AILogFunctionResult {
  success: boolean;
  message?: string;
  data?: Record<string, unknown>;
}

interface AILogButtonProps {
  // Plain-English instructions describing what this button is for and how
  // the AI should use the provided functions. Appended to a small built-in
  // base prompt that pins the "stay silent, only ask if missing required
  // info, then hang up" behaviour.
  prompt: string;
  // Functions the AI is allowed to call. The `endCall` tool is added
  // automatically; do not include it here.
  logFunctions: AILogFunction[];
  // Called every time a log function succeeds. Useful for the parent to
  // refresh state or close a form. Receives the function name + result.
  onLogged?: (functionName: string, result: AILogFunctionResult) => void;
  // Called once when the AI hangs up (regardless of whether anything was
  // logged). Use this for any final UI cleanup.
  onComplete?: () => void;
  // Optional className passthrough for the outer button.
  className?: string;
  // Tooltip text shown on hover.
  title?: string;
  // Optional: override the voice. Defaults to "Aoede".
  voiceName?: string;
  // Visual size. "md" (h-14, default) matches the form save buttons; "sm"
  // (h-11) matches the floating "+" Add Activity FAB.
  size?: "sm" | "md";
  // When true, suppresses the listening-state animations (the outer
  // animate-ping ring and the icon's animate-pulse). Useful when the
  // button sits inside a busy UI where the pulse is visually noisy.
  disablePulse?: boolean;
  // Called whenever the internal status changes. Useful for the parent
  // to react to listening/idle transitions.
  onStatusChange?: (
    status: "idle" | "connecting" | "listening" | "ending",
  ) => void;
}

type AIStatus = "idle" | "connecting" | "listening" | "ending";

const BASE_INSTRUCTION = [
  "You are a SILENT voice helper for a single logging task in the Peak X",
  "training app. The user controls the UI; your only job is to listen and",
  "call the provided log function.",
  "",
  "Behaviour rules:",
  "- DO NOT speak first. Do not greet, introduce yourself, or read",
  "  instructions. Stay completely silent and wait for the user.",
  "- The user will describe what they want logged. If you have everything",
  "  you need to call the provided log function, CALL IT IMMEDIATELY",
  "  without asking 'should I save it?' or reading the payload back.",
  "- After the log function returns, SAY NOTHING. Do not confirm, do not",
  "  say 'Logged' / 'Saved' / 'Done' / 'OK'. The UI handles the visual",
  "  confirmation and the call will be hung up automatically. Any spoken",
  "  reply at this point is a bug.",
  "- Only ask a clarifying question when a REQUIRED field is genuinely",
  "  missing. Combine missing fields into ONE short question. Otherwise,",
  "  stay silent.",
  "- Replies, when needed, must be ONE short sentence. No filler ('sure',",
  "  'absolutely', 'happy to help'). No preamble, no acknowledgement.",
  "- If the log function fails, briefly say what went wrong, ask for the",
  "  missing/corrected info, then try again. After 2 failed attempts,",
  "  call `endCall`.",
  "- If the user asks to cancel, stop, or hang up, call `endCall`",
  "  silently (no farewell).",
  "",
  "Task instructions:",
].join(" ");

const AILogButton: React.FC<AILogButtonProps> = ({
  prompt,
  logFunctions,
  onLogged,
  onComplete,
  className,
  title = "Log with AI",
  voiceName = "Aoede",
  size = "md",
  disablePulse = false,
  onStatusChange,
}) => {
  const sizeClasses = size === "sm" ? "h-8 w-8" : "h-10 w-10";
  const iconSize = size === "sm" ? "h-5 w-5" : "h-6 w-6";
  const [status, _setStatus] = useState<AIStatus>("idle");
  const setStatus = (newStatus: AIStatus) => {
    _setStatus(newStatus);
    onStatusChange?.(newStatus);
  };
  const [error, setError] = useState<string | null>(null);

  const sessionRef = useRef<LiveSession | null>(null);
  const controllerRef = useRef<AudioConversationController | null>(null);
  const endCallRef = useRef<() => Promise<void>>(async () => {});
  // Keep the latest props in refs so the function-calling handler (closed
  // over once when the call starts) always reads the up-to-date list and
  // callbacks without having to restart the session on every parent render.
  const logFunctionsRef = useRef<AILogFunction[]>(logFunctions);
  useEffect(() => {
    logFunctionsRef.current = logFunctions;
  }, [logFunctions]);
  const onLoggedRef = useRef<typeof onLogged>(onLogged);
  useEffect(() => {
    onLoggedRef.current = onLogged;
  }, [onLogged]);
  const onCompleteRef = useRef<typeof onComplete>(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const teardown = async () => {
    try {
      await controllerRef.current?.stop();
    } catch (stopError) {
      console.warn("AILogButton: error stopping audio controller:", stopError);
    }
    controllerRef.current = null;
    try {
      await sessionRef.current?.close();
    } catch (closeError) {
      console.warn("AILogButton: error closing live session:", closeError);
    }
    sessionRef.current = null;
  };

  const endCall = async () => {
    if (status === "idle") return;
    setStatus("ending");
    await teardown();
    setStatus("idle");
    onCompleteRef.current?.();
  };

  // Mirror the latest endCall closure so timeouts/handlers can call it.
  useEffect(() => {
    endCallRef.current = endCall;
  });

  // Cleanup any in-flight session when the button unmounts (e.g. a parent
  // form closes mid-call). Without this the mic stays hot.
  useEffect(() => {
    return () => {
      void teardown();
    };
  }, []);

  const handleFunctionCalls = async (
    calls: FunctionCall[],
  ): Promise<FunctionResponse> => {
    const call = calls[0];
    if (!call) {
      return {
        name: "unknown",
        response: { success: false, error: "No function call provided." },
      };
    }

    if (call.name === "endCall") {
      // Defer the actual hangup so this FunctionResponse (and any short
      // farewell audio) has time to flush before the socket closes.
      setTimeout(() => {
        void endCallRef.current();
      }, 400);
      return {
        id: call.id,
        name: call.name,
        response: { success: true, message: "Ending the call now." },
      };
    }

    const fn = logFunctionsRef.current.find(
      (f) => f.declaration.name === call.name,
    );
    if (!fn) {
      return {
        id: call.id,
        name: call.name,
        response: {
          success: false,
          error: `Unknown function: ${call.name}.`,
        },
      };
    }

    try {
      const result = await fn.handler(
        (call.args ?? {}) as Record<string, unknown>,
      );
      if (result.success) {
        onLoggedRef.current?.(call.name, result);
        // Hang up IMMEDIATELY on success so the model doesn't have a chance
        // to stream a spoken confirmation ("Logged.", "Saved.", etc.) back
        // through the audio channel. We tear the session down in parallel
        // with returning the FunctionResponse - by the time the model would
        // start generating audio, the session is already closing.
        void endCallRef.current();
      }
      return {
        id: call.id,
        name: call.name,
        response: {
          success: result.success,
          ...(result.message ? { message: result.message } : {}),
          ...(result.data ? { data: result.data } : {}),
        },
      };
    } catch (err: any) {
      console.error(`AILogButton: handler for ${call.name} threw:`, err);
      return {
        id: call.id,
        name: call.name,
        response: {
          success: false,
          error: err?.message ?? `Unknown error in ${call.name}.`,
        },
      };
    }
  };

  const startCall = async () => {
    if (status !== "idle") return;
    setError(null);
    setStatus("connecting");

    try {
      const tools: Tool[] = [
        {
          functionDeclarations: [
            ...logFunctionsRef.current.map((f) => f.declaration),
            {
              name: "endCall",
              description:
                "Hang up the call. Call this immediately after a successful log, or if the user asks to cancel/stop/hang up.",
            },
          ],
        },
      ];

      const liveModel = getLiveGenerativeModel(aiGoogle, {
        model: "gemini-3.1-flash-live-preview",
        generationConfig: {
          responseModalities: [ResponseModality.AUDIO],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: { voiceName },
            },
          },
        },
        systemInstruction: `${BASE_INSTRUCTION}\n${prompt}`,
        tools,
      });

      const session = await liveModel.connect();
      sessionRef.current = session;

      const controller = await startAudioConversation(session, {
        functionCallingHandler: handleFunctionCalls,
      });
      controllerRef.current = controller;

      setStatus("listening");
    } catch (startError: any) {
      console.error("AILogButton: failed to start voice call:", startError);
      const message =
        startError?.name === "NotAllowedError"
          ? "Microphone access denied."
          : startError?.name === "NotFoundError"
            ? "No microphone found."
            : startError?.message || "Could not start voice call.";
      setError(message);
      await teardown();
      setStatus("idle");
    }
  };

  const handleClick = () => {
    if (status === "idle") void startCall();
    else if (status === "listening") void endCall();
  };

  const isListening = status === "listening";
  const isConnecting = status === "connecting";
  const isEnding = status === "ending";
  const isBusy = isConnecting || isEnding;

  return (
    <div className="relative flex-shrink-0 p-4">
      <button
        type="button"
        onClick={handleClick}
        disabled={isBusy}
        // style={
        //   !isListening && !isConnecting
        //     ? {
        //         background: "linear-gradient(135deg, #7e22ce, #a855f7)",
        //         boxShadow: "0 4px 14px rgba(139, 92, 246, 0.45)",
        //       }
        //     : undefined
        // }
        className={`relative ${sizeClasses} rounded-full flex items-center justify-center transition-all ${
          isListening
            ? " text-red-500"
            : isConnecting
              ? "text-base-content cursor-wait bg-base-200"
              : "text-base-content hover:scale-105"
        } disabled:cursor-not-allowed `}
        title={
          isListening
            ? "Tap to cancel"
            : isConnecting
              ? "Connecting…"
              : isEnding
                ? "Ending…"
                : title
        }
        aria-label={title}
      >
        {isListening && !disablePulse && (
          <span className="absolute inset-0 rounded-full bg-red-500/40 animate-ping" />
        )}
        {isListening ? (
          <MicrophoneIcon
            className={`relative ${iconSize} ${disablePulse ? "" : "animate-pulse"}`}
          />
        ) : isConnecting ? (
          <span className="loading loading-spinner loading-sm text-white" />
        ) : (
          <SparklesIcon className={iconSize} />
        )}
      </button>
      {error && (
        <p
          role="alert"
          className="absolute right-0 top-full mt-1 text-[10px] text-red-500 font-grotesk whitespace-nowrap"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default AILogButton;
